import { useRef, useState, type ReactNode } from 'react'
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded'
import { FloatingMenu } from './FloatingMenu'
import { IconButton } from './IconButton'
import { Pressable } from './Pressable'

export type ButtonGroupTone = 'default' | 'danger' | 'save'

export type ButtonGroupItem = {
  key: string
  /** Text shown in the collapsed-mode dropdown menu. Also used as fallback title/aria-label. */
  label: string
  /** Icon shown on the pill button (uncollapsed) and on the collapsed primary face. */
  icon: ReactNode
  onClick: () => void
  disabled?: boolean
  title?: string
  ariaLabel?: string
  tone?: ButtonGroupTone
}

type ButtonGroupProps = {
  items: ButtonGroupItem[]
  /** Collapsed = first item shown as pill + chevron that opens a dropdown of all items. */
  collapsed?: boolean
  ariaLabel: string
  /** Required when collapsed — aria-label for the chevron trigger and menu. */
  menuAriaLabel?: string
  role?: 'group' | 'toolbar'
  className?: string
}

/**
 * Pill-shaped icon-button group used across the Manage board & review toolbars.
 *
 * Two rendering modes share the same `boardExplorerActionGroup` pill wrapper so
 * they visually match adjacent groups on the same row:
 *  - **uncollapsed**: renders each item as an `IconButton` — same
 *    DOM the toolbars used before this component existed.
 *  - **collapsed**: shows the FIRST item's icon (executes item 1 on click) plus
 *    a chevron that opens a dropdown listing every item (including 1) as
 *    text-only rows. Item 1 stays the face even if disabled; the chevron still
 *    opens the menu so users can reach the other actions.
 */
export const ButtonGroup = ({
  items,
  collapsed = false,
  ariaLabel,
  menuAriaLabel,
  role = 'group',
  className,
}: ButtonGroupProps) => {
  const [open, setOpen] = useState(false)
  const chevronRef = useRef<HTMLButtonElement | null>(null)

  const wrapperClass = className
    ? `boardExplorerActionGroup ${className}`
    : 'boardExplorerActionGroup'

  if (items.length === 0) return null

  if (!collapsed) {
    return (
      <div className={wrapperClass} role={role} aria-label={ariaLabel}>
        {items.map((item) => (
          <IconButton
            key={item.key}
            variant={item.tone === 'save' ? 'primary' : 'tertiary'}
            tone={item.tone === 'danger' ? 'danger' : undefined}
            disabled={item.disabled}
            label={item.ariaLabel ?? item.label}
            onClick={item.onClick}
            icon={item.icon}
          />
        ))}
      </div>
    )
  }

  const primary = items[0]
  const menuLabel = menuAriaLabel ?? `${ariaLabel} options`

  return (
    <div className={wrapperClass} role={role} aria-label={ariaLabel}>
      <IconButton
        variant={primary.tone === 'save' ? 'primary' : 'tertiary'}
        tone={primary.tone === 'danger' ? 'danger' : undefined}
        disabled={primary.disabled}
        label={primary.ariaLabel ?? primary.label}
        onClick={primary.onClick}
        icon={primary.icon}
      />
      <IconButton
        ref={chevronRef}
        size="sm"
        variant="tertiary"
        className="buttonGroupChevron"
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={menuLabel}
        label={menuLabel}
        icon={<ArrowDropDownRoundedIcon fontSize="inherit" />}
      />
      <FloatingMenu
        anchorRef={chevronRef}
        isOpen={open}
        onClose={() => setOpen(false)}
        placement="bottom-end"
        className="dsDrop is-open buttonGroupMenu"
        role="menu"
        ariaLabel={menuLabel}
      >
        <div className="dsDrop__items">
          {items.map((item) => (
            <Pressable
              key={item.key}
              role="menuitem"
              disabled={item.disabled}
              data-tone={item.tone === 'danger' ? 'warn' : undefined}
              className="dsDrop__item"
              onClick={() => {
                if (item.disabled) return
                item.onClick()
                setOpen(false)
              }}
            >
              <span className="dsDrop__itemLabel">{item.label}</span>
            </Pressable>
          ))}
        </div>
      </FloatingMenu>
    </div>
  )
}

export default ButtonGroup
